import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, UserPlus, Ticket, CreditCard, Check, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

// Simple browser fingerprint generator
const generateFingerprint = () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('fingerprint', 2, 2);
  const canvasData = canvas.toDataURL();

  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || '',
    canvasData.slice(-50),
  ].join('|');

  // Simple hash
  let hash = 0;
  for (let i = 0; i < components.length; i++) {
    const char = components.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
};

const SignupPage = () => {
  const [mode, setMode] = useState('choose'); // choose | code | subscribe
  const [enrollmentCode, setEnrollmentCode] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [age, setAge] = useState('');
  const [parentFirstName, setParentFirstName] = useState('');
  const [parentLastInitial, setParentLastInitial] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [deviceFingerprint, setDeviceFingerprint] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup, signupSubscribe } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setDeviceFingerprint(generateFingerprint());
  }, []);

  const formatEnrollmentCode = (value) => {
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (cleaned.length <= 5) return cleaned;
    if (cleaned.length <= 9) return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
    return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 9)}-${cleaned.slice(9, 13)}`;
  };

  const validateFields = () => {
    if (!name.trim() || !password.trim() || !age) {
      toast.error('Please fill in all fields!');
      return false;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match! Try again.");
      return false;
    }
    if (password.length < 4) {
      toast.error('Password should be at least 4 characters!');
      return false;
    }
    return true;
  };

  const validateParentFields = () => {
    if (!parentFirstName.trim()) {
      toast.error("Please enter parent's first name.");
      return false;
    }
    if (!parentLastInitial.trim()) {
      toast.error("Please enter parent's last name initial.");
      return false;
    }
    if (!parentEmail.trim() || !parentEmail.includes('@') || !parentEmail.includes('.')) {
      toast.error("Please enter a valid parent email address.");
      return false;
    }
    return true;
  };

  const handleCodeSignup = async (e) => {
    e.preventDefault();
    if (!enrollmentCode.trim()) {
      toast.error('Please enter your enrollment code!');
      return;
    }
    if (!validateFields()) return;

    setLoading(true);
    try {
      await signup(name.trim(), password, parseInt(age), enrollmentCode.trim());
      toast.success('Account created! Let\'s set up your profile!');
      navigate('/onboarding');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : 'Something went wrong. Try again!';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribeSignup = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;
    if (!validateParentFields()) return;

    setLoading(true);
    try {
      await signupSubscribe({
        name: name.trim(),
        password,
        age: parseInt(age),
        parent_first_name: parentFirstName.trim(),
        parent_last_initial: parentLastInitial.trim().charAt(0).toUpperCase(),
        parent_email: parentEmail.trim().toLowerCase(),
        device_fingerprint: deviceFingerprint,
      });
      toast.success('7-day free trial started! Let\'s set up your profile!');
      navigate('/onboarding');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : 'Something went wrong. Try again!';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Shared form fields
  const formFields = (
    <>
      <div className="space-y-2">
        <Label htmlFor="name" className="text-base font-bold text-slate-700">Pick a Nickname</Label>
        <Input
          id="name" type="text" value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your cool nickname"
          className="input-friendly"
          data-testid="signup-name-input"
          disabled={loading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="age" className="text-base font-bold text-slate-700">How Old Are You?</Label>
        <Select value={age} onValueChange={setAge} disabled={loading}>
          <SelectTrigger className="input-friendly" data-testid="signup-age-select">
            <SelectValue placeholder="Select your age" />
          </SelectTrigger>
          <SelectContent>
            {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map((a) => (
              <SelectItem key={a} value={String(a)}>{a} years old</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="password" className="text-base font-bold text-slate-700">Create a Secret Password</Label>
        <Input
          id="password" type="password" value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Your secret code"
          className="input-friendly"
          data-testid="signup-password-input"
          disabled={loading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmPassword" className="text-base font-bold text-slate-700">Confirm Password</Label>
        <Input
          id="confirmPassword" type="password" value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Type it again"
          className="input-friendly"
          data-testid="signup-confirm-password-input"
          disabled={loading}
        />
      </div>
    </>
  );

  // Parent info fields for subscribe form
  const parentFields = (
    <div className="space-y-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-4 h-4 text-blue-600" />
        <span className="text-sm font-bold text-slate-700">Parent / Guardian Info</span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2 space-y-1">
          <Label htmlFor="parentFirstName" className="text-sm font-semibold text-slate-600">First Name</Label>
          <Input
            id="parentFirstName" type="text" value={parentFirstName}
            onChange={(e) => setParentFirstName(e.target.value)}
            placeholder="Parent's first name"
            className="input-friendly"
            data-testid="signup-parent-first-name"
            disabled={loading}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="parentLastInitial" className="text-sm font-semibold text-slate-600">Last Initial</Label>
          <Input
            id="parentLastInitial" type="text" value={parentLastInitial}
            onChange={(e) => setParentLastInitial(e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 1).toUpperCase())}
            placeholder="L"
            className="input-friendly text-center uppercase"
            data-testid="signup-parent-last-initial"
            disabled={loading}
            maxLength={1}
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="parentEmail" className="text-sm font-semibold text-slate-600">Email</Label>
        <Input
          id="parentEmail" type="email" value={parentEmail}
          onChange={(e) => setParentEmail(e.target.value)}
          placeholder="parent@email.com"
          className="input-friendly"
          data-testid="signup-parent-email"
          disabled={loading}
        />
        <p className="text-[11px] text-slate-400">One free trial per parent email</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 relative" data-testid="signup-page">

      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <img 
            src="/brain-gym-logo.png" 
            alt="Brain Gym Jr. Logo" 
            className="w-28 h-28 mx-auto mb-3 animate-bounce-soft"
          />
          <h1 className="text-4xl md:text-5xl font-black text-slate-900 font-fredoka">
            Join Brain Gym Jr.
          </h1>
          <p className="text-lg text-slate-600 mt-2">Start your brain training adventure!</p>
        </div>

        {/* Choose Mode */}
        {mode === 'choose' && (
          <div className="space-y-4" data-testid="signup-choose-mode">
            <button
              onClick={() => setMode('subscribe')}
              className="w-full card-bubbly bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 hover:border-blue-400 transition-all text-left"
              data-testid="choose-subscribe-btn"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <CreditCard className="w-7 h-7 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 font-fredoka">Subscribe</h3>
                  <p className="text-sm text-slate-500">7-day free trial, then weekly subscription</p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold flex items-center gap-1"><Check className="w-3 h-3" /> 7 days free</span>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-semibold flex items-center gap-1"><Check className="w-3 h-3" /> No credit card required</span>
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-semibold flex items-center gap-1"><Check className="w-3 h-3" /> Cancel anytime</span>
              </div>
            </button>

            <button
              onClick={() => setMode('code')}
              className="w-full card-bubbly bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 hover:border-amber-400 transition-all text-left"
              data-testid="choose-code-btn"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center">
                  <Ticket className="w-7 h-7 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 font-fredoka">Enrollment Code</h3>
                  <p className="text-sm text-slate-500">Use a code from your parent or teacher</p>
                </div>
              </div>
            </button>

            <div className="text-center pt-2">
              <p className="text-slate-600">
                Already have an account?{' '}
                <Link to="/login" className="text-secondary font-bold hover:underline" data-testid="login-link">Log in!</Link>
              </p>
            </div>
          </div>
        )}

        {/* Subscribe Form */}
        {mode === 'subscribe' && (
          <div className="card-bubbly" data-testid="subscribe-form">
            <div className="flex items-center gap-2 mb-4">
              <button onClick={() => setMode('choose')} className="text-slate-400 hover:text-slate-600 text-sm font-bold">
                &larr; Back
              </button>
              <div className="flex-1 text-center">
                <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-bold">
                  <CreditCard className="w-4 h-4" /> Subscribe — 7 days free
                </span>
              </div>
            </div>

            <form onSubmit={handleSubscribeSignup} className="space-y-4">
              {formFields}
              {parentFields}

              <div className="bg-blue-50 rounded-xl p-3 text-center">
                <p className="text-sm text-blue-800 font-semibold">7-day free trial — then weekly subscription</p>
                <p className="text-xs text-blue-600 mt-1">Pay via dashboard after trial ends</p>
              </div>

              <button
                type="submit"
                className="btn-primary w-full flex items-center justify-center gap-2"
                disabled={loading}
                data-testid="subscribe-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Creating...</>
                ) : (
                  <><CreditCard className="w-5 h-5" /> Start Free Trial</>
                )}
              </button>
            </form>

            <div className="mt-4 text-center">
              <p className="text-slate-600 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-secondary font-bold hover:underline" data-testid="login-link-sub">Log in!</Link>
              </p>
            </div>
          </div>
        )}

        {/* Enrollment Code Form */}
        {mode === 'code' && (
          <div className="card-bubbly" data-testid="code-form">
            <div className="flex items-center gap-2 mb-4">
              <button onClick={() => setMode('choose')} className="text-slate-400 hover:text-slate-600 text-sm font-bold">
                &larr; Back
              </button>
              <div className="flex-1 text-center">
                <span className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-bold">
                  <Ticket className="w-4 h-4" /> Enrollment Code
                </span>
              </div>
            </div>

            <form onSubmit={handleCodeSignup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="enrollmentCode" className="text-base font-bold text-slate-700 flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-secondary" /> Enrollment Code
                </Label>
                <Input
                  id="enrollmentCode" type="text"
                  value={enrollmentCode}
                  onChange={(e) => setEnrollmentCode(formatEnrollmentCode(e.target.value))}
                  placeholder="BRAIN-XXXX-XXXX"
                  className="input-friendly font-mono text-lg tracking-wider uppercase"
                  data-testid="signup-enrollment-code-input"
                  disabled={loading}
                  maxLength={15}
                />
                <p className="text-sm text-slate-500">Ask your parent or teacher for this code!</p>
              </div>

              {formFields}

              <button
                type="submit"
                className="btn-primary w-full flex items-center justify-center gap-2"
                disabled={loading}
                data-testid="signup-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Creating...</>
                ) : (
                  <><UserPlus className="w-5 h-5" /> Create My Account</>
                )}
              </button>
            </form>

            <div className="mt-4 text-center">
              <p className="text-slate-600 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-secondary font-bold hover:underline" data-testid="login-link-code">Log in!</Link>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignupPage;
